import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings,
  User,
  Inbox,
  Rocket,
  UserMinus,
  CornerDownRight,
  History,
  Keyboard,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import type { SearchResultAction } from '@reflex/plugin-api';
import { toErrorMessage } from '@reflex/plugin-api/errors';
import { usePlugins } from '@/hooks/config/usePlugins';
import { useImpersonationStatus } from '@/hooks/auth/useImpersonationStatus';
import { useAuth } from '@/hooks/auth/useAuth';
import {
  buildMainNavItems,
  buildCoreMiscNavItems,
  adminNavItems,
  filterNavItemsByPermission,
} from '@/components/layout/NavBar/nav-items';
import { useNavSources } from '@/components/layout/NavBar/useNavSources';
import { ROUTES, withOrgSlug } from '@/lib/routes';
import { useActiveOrgSlug, useOrgRoutes } from '@/lib/useOrgRoutes';
import { useConfigureEntry } from '@/components/layout/ConfigureShell/useConfigureEntry';
import {
  buildConfigureCommandGroups,
  configureCommandRoutes,
} from '@/components/layout/ConfigureShell/configure-commands';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@reflex/ui/components/ui/command';
import { KbdCombo } from '@reflex/ui/components/shared/Kbd';
import { CommandPaletteSearchSection } from '@/components/layout/CommandPaletteSearchSection';
import { usePageCommands, type PageCommand } from '@/components/layout/page-commands';
import { rankCommandGroups, scoreCommandMatch } from '@/components/layout/command-palette-ranking';
import type { SessionJumpMode } from '@/lib/session-jump';
import { useHotkey } from '@/hooks/ui/useHotkey';
import { hotkeyKeys } from '@/lib/hotkeys';

const kbdHintClassName =
  'border-border/80 bg-gradient-to-b from-muted/60 to-muted font-semibold text-foreground shadow-sm';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Opens the session-jump palette. Optional so the palette still renders
   * standalone (stories, embedded uses) without the jump surface mounted.
   */
  onJumpToSession?: (mode: SessionJumpMode) => void;
  /** Opens the keyboard-shortcut listing. Optional for the same reason. */
  onViewHotkeys?: () => void;
}

interface CommandEntry {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  group?: string;
  /** Extra search terms so old names still find the renamed surface. */
  keywords?: string[];
  /**
   * Page-scoped entries act in place instead of navigating; `to` stays a
   * synthetic unique key. Dispatched like actions (deferred past close).
   */
  run?: () => void;
}

interface ActionEntry {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Extra terms the fuzzy scorer matches this action on. */
  keywords: readonly string[];
  action: (navigate: ReturnType<typeof useNavigate>) => void;
  /** Shortcut hint rendered at the end of the row, when the action has one. */
  shortcut?: readonly string[];
}

const accountCommands: CommandEntry[] = [
  { label: 'Profile', to: ROUTES.profile, icon: User },
  { label: 'Settings', to: ROUTES.settings, icon: Settings },
];

/** Old product names that should still match after renames. */
const COMMAND_ALIASES: Readonly<Record<string, readonly string[]>> = {
  Automations: ['Flows'],
  Configure: ['Settings'],
  Environments: ['Blueprints'],
  Credentials: ['Secrets', 'API keys', 'Model providers'],
  MCP: ['MCP Servers'],
  Agents: ['Personas', 'Models'],
};

const LAUNCH_AGENT_KEYWORDS = ['launch', 'new', 'agent', 'create', 'start'] as const;
const JUMP_SESSION_KEYWORDS = ['jump', 'session', 'agent', 'open', 'go to', 'find'] as const;
const RECENT_SESSION_KEYWORDS = ['recent', 'session', 'agent', 'history', 'last'] as const;
const VIEW_HOTKEYS_KEYWORDS = [
  'hotkeys',
  'shortcuts',
  'keyboard',
  'keys',
  'keybindings',
  'bindings',
] as const;

const adminCoreCommands: CommandEntry[] = [...adminNavItems];

function mergeDeduped(
  core: CommandEntry[],
  group: string,
  pluginCommands: CommandEntry[],
): CommandEntry[] {
  const coreRoutes = new Set(core.map((c) => c.to));
  return [...core, ...pluginCommands.filter((c) => c.group === group && !coreRoutes.has(c.to))];
}

/** Merge per-entry keywords with rename aliases used by the fuzzy scorer. */
function withSearchKeywords(entry: CommandEntry): CommandEntry {
  return {
    ...entry,
    keywords: [...(entry.keywords ?? []), ...(COMMAND_ALIASES[entry.label] ?? [])],
  };
}

export function CommandPalette({
  open,
  onOpenChange,
  onJumpToSession,
  onViewHotkeys,
}: CommandPaletteProps) {
  const navigate = useNavigate();
  const { commands: pluginCommands, searchProviders } = usePlugins();
  const { can, isPlatformAdmin, authEnabled, activeMembership } = useAuth();
  const slug = useActiveOrgSlug();
  const orgRoutes = useOrgRoutes();
  const configureEntry = useConfigureEntry();
  const { configureDrillGroups } = useNavSources();
  const membershipTeams = activeMembership?.teams;
  // Host-owned impersonation status — kept independent of the impersonation
  // plugin's web manifest so this escape hatch survives even if the
  // plugin's globalUiElements get stripped (which happens by design while
  // an actor's admin bit is dropped during a session).
  const {
    isActive: isImpersonating,
    stop: stopImpersonation,
    isStopping,
  } = useImpersonationStatus();
  const [rawQuery, setRawQuery] = useState('');
  const trimmedQuery = rawQuery.trim();

  // Plugin-declared commands carry root-relative paths (e.g. `/chat`); rewrite
  // them to the active org's slug-scoped path so they navigate correctly.
  const scopedPluginCommands = useMemo<CommandEntry[]>(
    () =>
      pluginCommands.map((c) => ({
        ...c,
        // Hash commands dispatch `plugin:command` events — keep them verbatim
        // (same rule as UserSection) instead of org-scoping to `/orgs/:slug/#…`.
        to: c.to.startsWith('#') || c.rootLevel ? c.to : withOrgSlug(slug, c.to),
      })),
    [pluginCommands, slug],
  );

  const actionCommands = useMemo<ActionEntry[]>(
    () => [
      {
        label: 'Launch Agent',
        icon: Rocket,
        keywords: LAUNCH_AGENT_KEYWORDS,
        action: (nav) => nav(orgRoutes.runs, { state: { openLaunchDialog: true } }),
      },
      // The jump palette is keyboard-first, so these two rows exist to teach
      // the shortcut: they carry the hint that makes ⌘K the last time you need
      // the palette to reach a session.
      ...(onJumpToSession
        ? ([
            {
              label: 'Jump to Session',
              icon: Search,
              keywords: JUMP_SESSION_KEYWORDS,
              shortcut: hotkeyKeys('jumpToSession'),
              action: () => onJumpToSession('all'),
            },
            {
              label: 'Recent Sessions',
              icon: History,
              keywords: RECENT_SESSION_KEYWORDS,
              shortcut: hotkeyKeys('recentSessions'),
              action: () => onJumpToSession('recent'),
            },
          ] satisfies ActionEntry[])
        : []),
      ...(onViewHotkeys
        ? ([
            {
              label: 'View Hotkeys',
              icon: Keyboard,
              keywords: VIEW_HOTKEYS_KEYWORDS,
              shortcut: hotkeyKeys('viewHotkeys'),
              action: () => onViewHotkeys(),
            },
          ] satisfies ActionEntry[])
        : []),
    ],
    [orgRoutes.runs, onJumpToSession, onViewHotkeys],
  );

  // Whatever page is mounted right now may contribute commands (tab switches,
  // page-local actions) through the page-commands registry. The palette only
  // sees label/icon/run — it stays ignorant of who registered them. They rank
  // as one more group among the static groups (last in IA order), so a page
  // with many actions cannot crowd out global search or the Actions block.
  const pageCommands = usePageCommands();
  const pageCommandEntries = useMemo<CommandEntry[]>(
    () =>
      pageCommands.map((command) => ({
        label: command.label,
        to: `#page:${command.id}`,
        icon: command.icon ?? CornerDownRight,
        keywords: command.keywords ? [...command.keywords] : undefined,
        run: command.run,
      })),
    [pageCommands],
  );

  const filteredActionCommands = useMemo(
    () =>
      actionCommands.filter(
        (entry) => scoreCommandMatch(entry.label, trimmedQuery, entry.keywords) > 0,
      ),
    [actionCommands, trimmedQuery],
  );

  // Configure groups share the sidebar's mapping table so the two surfaces
  // cannot drift. Empty slug collapses the groups rather than broken links.
  // One Team secrets row per membership team so Cmd+K is not stuck on
  // whichever team happens to be first in the membership list.
  const configureGroups = useMemo(
    () => buildConfigureCommandGroups(slug, configureDrillGroups, { teams: membershipTeams }),
    [slug, configureDrillGroups, membershipTeams],
  );
  const configureRoutes = useMemo(() => configureCommandRoutes(configureGroups), [configureGroups]);

  const navigationCommands = useMemo<CommandEntry[]>(
    () => [
      ...buildMainNavItems(slug),
      { label: 'Inbox', to: orgRoutes.inbox, icon: Inbox },
      {
        label: configureEntry.label,
        to: configureEntry.to,
        icon: configureEntry.icon,
        keywords: ['Settings'],
      },
    ],
    [slug, orgRoutes.inbox, configureEntry],
  );
  const miscCoreCommands = useMemo<CommandEntry[]>(() => [...buildCoreMiscNavItems(slug)], [slug]);

  // Mirror the sidebar's permission gating (`useNavSources`) so the palette
  // never surfaces routes the user can't actually visit. When auth is
  // disabled (single-tenant default) every item stays visible.
  // Configure groups already arrive permission-filtered — do not double-filter.
  const filterByPerm = useMemo(
    () =>
      <T,>(items: readonly T[]): T[] =>
        authEnabled ? filterNavItemsByPermission(items, can, isPlatformAdmin) : [...items],
    [authEnabled, can, isPlatformAdmin],
  );

  // Drop plugin commands whose routes already appear under Configure so
  // billing / MCP / Tailscale stop landing in Navigation / Misc.
  const pluginCommandsOutsideConfigure = useMemo(
    () => scopedPluginCommands.filter((c) => !configureRoutes.has(c.to)),
    [scopedPluginCommands, configureRoutes],
  );

  const allNavigationCommands = useMemo<CommandEntry[]>(
    () =>
      filterByPerm(mergeDeduped(navigationCommands, 'navigation', pluginCommandsOutsideConfigure)),
    [navigationCommands, pluginCommandsOutsideConfigure, filterByPerm],
  );
  const allMiscCommands = useMemo<CommandEntry[]>(
    () => filterByPerm(mergeDeduped(miscCoreCommands, 'misc', pluginCommandsOutsideConfigure)),
    [miscCoreCommands, pluginCommandsOutsideConfigure, filterByPerm],
  );
  const allAdminCommands = useMemo<CommandEntry[]>(
    () => filterByPerm(mergeDeduped(adminCoreCommands, 'admin', pluginCommandsOutsideConfigure)),
    [pluginCommandsOutsideConfigure, filterByPerm],
  );
  const allAccountCommands = useMemo<CommandEntry[]>(
    () => filterByPerm(mergeDeduped(accountCommands, 'settings', pluginCommandsOutsideConfigure)),
    [pluginCommandsOutsideConfigure, filterByPerm],
  );

  // Admin routes live at the platform tier — match the sidebar drill group
  // which is gated behind `requirePlatformAdmin`. Hide the whole section
  // for non-admins instead of relying solely on per-item permission filters
  // (some admin items have permissions a regular org admin could hold).
  const showAdminGroup = !authEnabled || isPlatformAdmin;

  // Score with cmdk's defaultFilter, then reorder groups/items so the best
  // label match leads the list (and keyboard selection). Providers keep
  // owning their own rows above this block.
  const rankedStaticGroups = useMemo(() => {
    const groups = [
      { label: 'Navigation', items: allNavigationCommands.map(withSearchKeywords) },
      ...configureGroups.map((group) => ({
        label: group.label,
        items: group.items.map((item) =>
          withSearchKeywords({
            label: item.label,
            to: item.to,
            icon: item.icon,
          }),
        ),
      })),
      { label: 'Misc', items: allMiscCommands.map(withSearchKeywords) },
      ...(showAdminGroup
        ? [{ label: 'Admin', items: allAdminCommands.map(withSearchKeywords) }]
        : []),
      { label: 'Account', items: allAccountCommands.map(withSearchKeywords) },
      // Last in IA order so an action-heavy page never buries the global
      // groups; a typed query still lifts it when it holds the best match.
      ...(pageCommandEntries.length > 0
        ? [{ label: 'On this page', items: pageCommandEntries }]
        : []),
    ];
    return rankCommandGroups(groups, trimmedQuery);
  }, [
    allNavigationCommands,
    configureGroups,
    allMiscCommands,
    allAdminCommands,
    allAccountCommands,
    showAdminGroup,
    pageCommandEntries,
    trimmedQuery,
  ]);

  useHotkey('commandPalette', () => onOpenChange(true));

  // Reset the query whenever the palette closes so the next open starts clean.
  const handleOpenChange = (next: boolean) => {
    if (!next) setRawQuery('');
    onOpenChange(next);
  };

  const runCommand = (to: string) => {
    handleOpenChange(false);
    if (to.startsWith('#')) {
      setTimeout(() => window.dispatchEvent(new CustomEvent('plugin:command', { detail: to })), 0);
    } else {
      navigate(to);
    }
  };

  const runPageCommand = (run: PageCommand['run']) => {
    handleOpenChange(false);
    // Same deferral as actions: let the dialog unmount before the page reacts.
    window.setTimeout(run, 0);
  };

  const runAction = (action: ActionEntry['action']) => {
    handleOpenChange(false);
    // Defer until the palette dialog has unmounted so the launch dialog does
    // not treat the same pointer/keyboard gesture as an outside dismiss.
    window.setTimeout(() => action(navigate), 0);
  };

  const runStopImpersonation = () => {
    handleOpenChange(false);
    stopImpersonation()
      .then(() => toast.success('Stopped impersonating'))
      .catch((err: unknown) =>
        toast.error('Failed to stop impersonating', {
          description: toErrorMessage(err),
        }),
      );
  };

  const runSearchResult = (result: SearchResultAction) => {
    handleOpenChange(false);
    if (result.kind === 'navigate') {
      navigate(withOrgSlug(slug, result.to));
    } else if (result.kind === 'command') {
      setTimeout(
        () => window.dispatchEvent(new CustomEvent('plugin:command', { detail: result.hash })),
        0,
      );
    } else {
      result.run();
    }
  };

  const renderGroup = (heading: string, items: readonly CommandEntry[]) => {
    if (items.length === 0) return null;

    return (
      <CommandGroup heading={heading}>
        {items.map(({ label, to, icon: Icon, keywords, run }) => (
          <CommandItem
            key={to}
            // Page-scoped labels (Chat, Details, ...) can repeat a global
            // row's label; the synthetic `to` keeps cmdk values unique.
            value={run ? to : label}
            keywords={keywords}
            onSelect={() => (run ? runPageCommand(run) : runCommand(to))}
          >
            <Icon className="h-4 w-4" />
            {label}
          </CommandItem>
        ))}
      </CommandGroup>
    );
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={handleOpenChange}
      showCloseButton={false}
      shouldFilter={false}
    >
      <CommandInput
        placeholder="Search pages, commands, and personas..."
        value={rawQuery}
        onValueChange={setRawQuery}
      />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>No results found.</CommandEmpty>
        {isImpersonating && (
          <>
            <CommandGroup heading="Session">
              <CommandItem
                data-testid="command-stop-impersonation"
                onSelect={runStopImpersonation}
                disabled={isStopping}
              >
                <UserMinus className="h-4 w-4" />
                {isStopping ? 'Stopping impersonation…' : 'Stop impersonating'}
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
          </>
        )}
        {filteredActionCommands.length > 0 ? (
          <>
            <CommandGroup heading="Actions">
              {filteredActionCommands.map(({ label, icon: Icon, keywords, shortcut, action }) => (
                <CommandItem
                  key={label}
                  value={label}
                  keywords={[...keywords]}
                  onSelect={() => runAction(action)}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                  {shortcut ? (
                    <KbdCombo keys={[...shortcut]} size="sm" className="ml-auto" />
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        ) : null}
        {searchProviders.map((provider) => (
          <CommandPaletteSearchSection
            key={provider.id}
            provider={provider}
            query={trimmedQuery}
            onSelect={runSearchResult}
          />
        ))}
        {searchProviders.length > 0 ? <CommandSeparator /> : null}
        {rankedStaticGroups.map((group, index) => (
          <div key={group.label}>
            {index > 0 ? <CommandSeparator /> : null}
            {renderGroup(group.label, group.items)}
          </div>
        ))}
      </CommandList>
      <div
        data-testid="command-palette-kbd-hints"
        className="hidden items-center justify-end gap-4 border-t border-border bg-muted/30 px-3 py-2 md:flex"
      >
        <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <KbdCombo keys={['up', 'down']} size="sm" keyClassName={kbdHintClassName} />
          <span>navigate</span>
        </div>
        <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <KbdCombo keys={['enter']} size="sm" keyClassName={kbdHintClassName} />
          <span>select</span>
        </div>
        <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <KbdCombo keys={['esc']} size="sm" keyClassName={kbdHintClassName} />
          <span>close</span>
        </div>
      </div>
    </CommandDialog>
  );
}
